/**
 * Vendor Entity Hooks - Service Request Management
 *
 * React hooks for vendor service request management including:
 * - Viewing available service requests
 * - Submitting responses and bids
 * - Managing work orders
 * - File uploads and portfolio management
 */

import { useState, useEffect, useCallback } from 'react';
import { ServiceRequest } from '@/types/shared/serviceRequest';
import {
  VendorWorkOrder,
  WorkOrderStatus,
  WorkOrderPriority,
} from '@/types/entities/vendor/workOrders';
import {
  serviceRequestService,
  CreateServiceRequestData as VendorServiceRequestFilters,
  CreateServiceRequestResponseData as VendorResponseData,
} from '@/services/entities/vendor/serviceRequestService';

export interface VendorPortfolioItem {
  id: string;
  title: string;
  description: string;
  imageFile?: File;
  imageUrl?: string;
  createdAt: string;
}

// Define VendorWorkOrderUpdate interface locally
export interface VendorWorkOrderUpdate {
  workOrderId: string;
  status?: string;
  progress?: number;
  notes?: string;
}

// ====== VENDOR SERVICE REQUEST HOOKS ======

export interface UseVendorServiceRequestsOptions {
  vendorId: string;
  autoFetch?: boolean;
  filters?: VendorServiceRequestFilters;
}

/**
 * Hook for managing vendor service requests (both available and assigned)
 */
export function useVendorServiceRequests(options: UseVendorServiceRequestsOptions) {
  const { vendorId, autoFetch = true, filters } = options;

  const [availableRequests, setAvailableRequests] = useState<ServiceRequest[]>([]);
  const [assignedRequests, setAssignedRequests] = useState<ServiceRequest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAvailableRequests = useCallback(
    async (customFilters?: VendorServiceRequestFilters) => {
      setIsLoading(true);
      setError(null);

      try {
        const requests = await serviceRequestService.getServiceRequests(customFilters || filters);
        setAvailableRequests(requests);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch available requests');
      } finally {
        setIsLoading(false);
      }
    },
    [filters],
  );

  const fetchAssignedRequests = useCallback(
    async (customFilters?: VendorServiceRequestFilters) => {
      setIsLoading(true);
      setError(null);

      try {
        const requests = await serviceRequestService.getServiceRequests(customFilters || filters);
        setAssignedRequests(requests);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch assigned requests');
      } finally {
        setIsLoading(false);
      }
    },
    [filters],
  );

  const fetchAllRequests = useCallback(
    async (customFilters?: VendorServiceRequestFilters) => {
      await Promise.all([
        fetchAvailableRequests(customFilters),
        fetchAssignedRequests(customFilters),
      ]);
    },
    [fetchAvailableRequests, fetchAssignedRequests],
  );

  useEffect(() => {
    if (autoFetch && vendorId) {
      fetchAllRequests();
    }
  }, [autoFetch, vendorId, fetchAllRequests]);

  return {
    availableRequests,
    assignedRequests,
    isLoading,
    error,
    fetchAvailableRequests,
    fetchAssignedRequests,
    fetchAllRequests,
    refresh: fetchAllRequests,
  };
}

/**
 * Hook for individual service request details
 */
export function useVendorServiceRequest(id: string) {
  const [serviceRequest, setServiceRequest] = useState<ServiceRequest | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchServiceRequest = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const request = await serviceRequestService.getServiceRequestById(id);
      setServiceRequest(request);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch service request');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchServiceRequest();
  }, [fetchServiceRequest]);

  return {
    serviceRequest,
    isLoading,
    error,
    refresh: fetchServiceRequest,
  };
}

// ====== VENDOR RESPONSE HOOKS ======

/**
 * Hook for submitting vendor responses to service requests
 */
export function useVendorServiceResponse() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitResponse = useCallback(async (data: VendorResponseData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await serviceRequestService.createServiceRequestResponse(data);
      return response;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit response');
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const updateResponse = useCallback(
    async (serviceRequestId: string, responseId: string, data: Partial<VendorResponseData>) => {
      setIsSubmitting(true);
      setError(null);

      try {
        // const response = await serviceRequestService.updateServiceResponse(serviceRequestId, responseId, data);
        throw new Error('Update service response not implemented yet');
        // return response;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update response');
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [],
  );

  return {
    submitResponse,
    updateResponse,
    isSubmitting,
    error,
  };
}

// ====== VENDOR WORK ORDER HOOKS ======

/**
 * Hook for vendor work order management
 */
export function useVendorWorkOrders(vendorId: string) {
  const [workOrders, setWorkOrders] = useState<VendorWorkOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkOrders = useCallback(
    async (filters?: { status?: WorkOrderStatus; priority?: WorkOrderPriority }) => {
      setIsLoading(true);
      setError(null);

      try {
        // const orders = await serviceRequestService.getVendorWorkOrders(vendorId, filters);
        // Placeholder for work orders functionality
        const orders: VendorWorkOrder[] = [];
        setWorkOrders(orders);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch work orders');
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const updateWorkOrder = useCallback(async (data: VendorWorkOrderUpdate) => {
    setIsLoading(true);
    setError(null);

    try {
      // const updatedOrder = await serviceRequestService.updateWorkOrder(data);
      // Placeholder return for now - simplified mock response
      const updatedOrder: Partial<VendorWorkOrder> = {
        id: data.workOrderId,
        status: 'in_progress' as WorkOrderStatus,
        vendorId: '',
        title: 'Updated Work Order',
        description: 'Work order has been updated',
        priority: 'medium' as WorkOrderPriority,
        updatedAt: new Date().toISOString(),
      };
      setWorkOrders((prev) =>
        prev.map((order) =>
          order.id === data.workOrderId ? { ...order, ...updatedOrder } : order,
        ),
      );
      return updatedOrder;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update work order');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (vendorId) {
      fetchWorkOrders();
    }
  }, [vendorId, fetchWorkOrders]);

  return {
    workOrders,
    isLoading,
    error,
    fetchWorkOrders,
    updateWorkOrder,
    refresh: fetchWorkOrders,
  };
}

// ====== VENDOR FILE UPLOAD HOOKS ======

/**
 * Hook for vendor file uploads (portfolio, work order photos, etc.)
 */
export function useVendorFileUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const uploadFile = useCallback(
    async (file: File, context?: 'portfolio' | 'response' | 'work_order') => {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadError(null);

      try {
        // Simulate upload progress for better UX
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => Math.min(prev + 10, 90));
        }, 100);

        const result = await serviceRequestService.uploadFile(file);

        clearInterval(progressInterval);
        setUploadProgress(100);

        return result;
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Failed to upload file');
        throw err;
      } finally {
        setIsUploading(false);
        setTimeout(() => setUploadProgress(0), 1000);
      }
    },
    [],
  );

  return {
    uploadFile,
    isUploading,
    uploadProgress,
    uploadError,
  };
}

// ====== VENDOR PORTFOLIO HOOKS ======

/**
 * Hook for managing vendor portfolio items
 */
export function useVendorPortfolio() {
  const [portfolioItems, setPortfolioItems] = useState<VendorPortfolioItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addPortfolioItem = useCallback(
    async (item: { title: string; description: string; imageFile?: File; imageUrl?: string }) => {
      setIsLoading(true);
      setError(null);

      try {
        // This would typically create a portfolio item via API
        // For now, we'll just add it to local state
        const newItem = {
          id: Date.now().toString(),
          ...item,
          createdAt: new Date().toISOString(),
        };
        setPortfolioItems((prev) => [newItem, ...prev]);
        return newItem;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add portfolio item');
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const removePortfolioItem = useCallback(async (id: string) => {
    setPortfolioItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  return {
    portfolioItems,
    isLoading,
    error,
    addPortfolioItem,
    removePortfolioItem,
  };
}
